import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Stack, Typography} from 'views/components';
import {TaskEditor, Translate, translate} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';

import backend from 'utils/backend';
import moment from 'moment';

const AllTasks = (props) => {
  const {
    user,
    taskRefreshSetAllTasksCount,
    taskRefreshIncrementAllTasksCount,
    taskRefreshDecrementAllTasksCount,
    statusProgressWrap: progressWrap,
  } = props;

  const [lastWeekTasks, setLastWeekTasks] = React.useState([]);
  const [todayTasks, setTodayTasks] = React.useState([]);
  const [thisWeekTasks, setThisWeekTasks] = React.useState([]);
  const [dueTodayCount, setDueTodayCount] = React.useState(0);
  const [totalTaskCount, setTotalTaskCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editorSection, setEditorSection] = React.useState(null);
  const [editingTaskRn, setEditingTaskRn] = React.useState(null);

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setLastWeekTasks([]);
      setTodayTasks([]);
      setThisWeekTasks([]);
      setDueTodayCount(0);
      setTotalTaskCount(0);
      taskRefreshSetAllTasksCount(0);
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const oneWeekAgoUtc = moment().startOf('day').subtract(1, 'week').utc();
        const startOfTodayUtc = moment().startOf('day').utc();
        const endOfTodayUtc = moment().endOf('day').utc();
        const endOfWeekUtc = moment().endOf('isoWeek').utc();

        const startDate = Math.floor(new Date(oneWeekAgoUtc.toISOString()).getTime());
        const endOfWeekDate = Math.floor(new Date(endOfWeekUtc.toISOString()).getTime());
        const taskSortBy = {sortKey: 'dueDate', sortDirection: 'ASC'};
        const companyTaskFilters = [
          {key: 'dueDate', value: `[${startDate} TO ${endOfWeekDate}]`},
          {key: 'status', value: ['OPEN', 'COMPLETE']},
        ];
        let allTasks = [];
        let lastEvaluatedKey = null;

        while (lastEvaluatedKey !== undefined) {
          const companyTasksResponse = await backend.task.queryCompanyTasks(companyTaskFilters, taskSortBy, lastEvaluatedKey);

          allTasks = [...allTasks, ...(companyTasksResponse?.list || [])];
          lastEvaluatedKey = companyTasksResponse?.lastEvaluatedKey;

          if (!lastEvaluatedKey) {
            break;
          }
        }

        const nextLastWeekTasks = allTasks.filter((task) => (
          task.status !== 'COMPLETE' && moment(task.dueDate).isBefore(startOfTodayUtc)
        ));
        const nextTodayTasks = allTasks.filter((task) => (
          moment(task.dueDate).isBetween(startOfTodayUtc, endOfTodayUtc, null, '[]')
        ));
        const {openTasks, completeTasks} = nextTodayTasks.reduce((acc, todayTask) => {
          if (todayTask.status === 'OPEN') {
            acc.openTasks.push(todayTask);
          } else if (todayTask.status === 'COMPLETE') {
            acc.completeTasks.push(todayTask);
          }
          return acc;
        }, {openTasks: [], completeTasks: []});
        const nextThisWeekTasks = allTasks.filter((task) => (
          moment(task.dueDate).isAfter(endOfTodayUtc) && moment(task.dueDate).isSameOrBefore(endOfWeekUtc)
        ));
        const allTasksCount = allTasks.filter((task) => task.status !== 'COMPLETE').length;

        setLastWeekTasks(nextLastWeekTasks);
        setTodayTasks([...openTasks, ...completeTasks]);
        setThisWeekTasks(nextThisWeekTasks);
        setDueTodayCount(nextTodayTasks.filter((task) => task.status !== 'COMPLETE').length);
        setTotalTaskCount(allTasksCount);
        taskRefreshSetAllTasksCount(allTasksCount);
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, taskRefreshSetAllTasksCount, user?.userRn]);

  const hydrateTaskAssignees = React.useCallback(async (taskData) => {
    const connectionRns = taskData.connections || [];
    const ownerRns = taskData.ownerRns || [];

    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
        try {
          const clientResponse = await backend.client.getClient(connectionRn);
          const clientDetail = clientResponse?.clientDetail;
          const [firstName, ...lastNameParts] = clientDetail?.clientName.split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
        try {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn);
          const caregiverDetail = caregiverResponse?.caregiverDetail;
          const caregiverName = caregiverDetail?.caregiverName || '';
          const [firstName, ...lastNameParts] = caregiverName.trim().split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      return null;
    }));

    const ownerInfo = await Promise.all(ownerRns.map(async (ownerRn) => {
      try {
        const adminResponse = await backend.user.getUser(ownerRn);

        return {
          ownerRn,
          firstName: adminResponse.user.firstName,
          lastName: adminResponse.user.lastName,
        };
      } catch {
        return null;
      }
    }));

    return {
      ...taskData,
      connectionInfo: connectionInfo.filter(Boolean),
      ownerInfo: ownerInfo.filter(Boolean),
    };
  }, []);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const openDialogs = {
    addTask: (section) => {
      setEditingTaskRn(null);
      setIsEditorOpen(true);
      setEditorSection(section);
    },
    editTask: (task) => {
      setIsEditorOpen(false);
      setEditorSection(null);
      setEditingTaskRn(task.taskRn);
    },
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorSection(null);
    setEditingTaskRn(null);
  };

  const handleAddTask = (taskData) => {
    progressWrap(async () => {
      const createdTaskResponse = await backend.task.addTask(taskData);
      const createdTask = await hydrateTaskAssignees(createdTaskResponse.task);
      const createdDueDate = moment(createdTask?.dueDate);

      closeEditor();

      if (!createdDueDate.isValid()) {
        return;
      }

      const oneWeekAgoUtc = moment().startOf('day').subtract(1, 'week').utc();
      const startOfTodayUtc = moment().startOf('day').utc();
      const endOfTodayUtc = moment().endOf('day').utc();
      const endOfWeekUtc = moment().endOf('isoWeek').utc();
      const withinVisibleRange = createdDueDate.isBetween(oneWeekAgoUtc, endOfWeekUtc, null, '[]');

      if (!withinVisibleRange) {
        return;
      }

      if (createdDueDate.isBefore(startOfTodayUtc) && createdTask.status !== 'COMPLETE') {
        setLastWeekTasks((previousTasks) =>
          [...previousTasks, createdTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf()),
        );
      }

      if (createdDueDate.isBetween(startOfTodayUtc, endOfTodayUtc, null, '[]')) {
        setTodayTasks((previousTasks) =>
          [...previousTasks, createdTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf()),
        );
        if (createdTask.status !== 'COMPLETE') {
          setDueTodayCount((previousCount) => previousCount + 1);
        }
      }

      if (createdDueDate.isAfter(endOfTodayUtc) && createdDueDate.isSameOrBefore(endOfWeekUtc)) {
        setThisWeekTasks((previousTasks) =>
          [...previousTasks, createdTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf()),
        );
      }

      if (createdTask.status !== 'COMPLETE') {
        setTotalTaskCount((previousCount) => previousCount + 1);
        taskRefreshIncrementAllTasksCount();
      }
    }, {debugString: 'CREATE_TASK'});
  };

  const handleEditTask = (taskRn, taskData) => {
    const currentTask = [
      ...lastWeekTasks,
      ...todayTasks,
      ...thisWeekTasks,
    ].find((task) => task.taskRn === taskRn);

    progressWrap(async () => {
      try {
        const updatedTaskResponse = await backend.task.updateTask(taskRn, taskData);
        const updatedTask = await hydrateTaskAssignees(updatedTaskResponse.task);
        const nextDueDate = moment(updatedTask.dueDate);

        setLastWeekTasks((previousTasks) => {
          const filteredTasks = previousTasks.filter((task) => task.taskRn !== taskRn);

          if (!nextDueDate.isBefore(moment().startOf('day').utc()) || updatedTask.status === 'COMPLETE') {
            return filteredTasks;
          }

          return [...filteredTasks, updatedTask];
        });
        setTodayTasks((previousTasks) => {
          const filteredTasks = previousTasks.filter((task) => task.taskRn !== taskRn);

          if (!nextDueDate.isSame(moment(), 'day')) {
            return filteredTasks;
          }

          return [...filteredTasks, updatedTask];
        });
        setThisWeekTasks((previousTasks) => {
          const filteredTasks = previousTasks.filter((task) => task.taskRn !== taskRn);

          if (nextDueDate.isBefore(moment().startOf('day').utc()) || nextDueDate.isSame(moment(), 'day')) {
            return filteredTasks;
          }

          return [...filteredTasks, updatedTask];
        });
        setDueTodayCount((previousCount) => {
          const previousDueDate = moment(currentTask.dueDate);
          const previousToday = currentTask.status !== 'COMPLETE' && previousDueDate.isSame(moment(), 'day');
          const nextToday = updatedTask.status !== 'COMPLETE' && nextDueDate.isSame(moment(), 'day');

          return Math.max(0, previousCount - (previousToday ? 1 : 0) + (nextToday ? 1 : 0));
        });
        if (currentTask.status !== 'COMPLETE' && updatedTask.status === 'COMPLETE') {
          setTotalTaskCount((previousCount) => Math.max(0, previousCount - 1));
          taskRefreshDecrementAllTasksCount();
        }
        if (currentTask.status === 'COMPLETE' && updatedTask.status !== 'COMPLETE') {
          setTotalTaskCount((previousCount) => previousCount + 1);
          taskRefreshIncrementAllTasksCount();
        }
      } finally {
        closeEditor();
      }
    });
  };

  const handleRemoveTask = (taskData) => {
    const dueDate = moment(taskData.dueDate);
    const isIncomplete = taskData.status !== 'COMPLETE';

    setLastWeekTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));
    setTodayTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));
    setThisWeekTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));
    if (isIncomplete && dueDate.isSame(moment(), 'day')) {
      setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
    }
    if (isIncomplete) {
      setTotalTaskCount((previousCount) => Math.max(0, previousCount - 1));
      taskRefreshDecrementAllTasksCount();
    }

    progressWrap(async () => {
      try {
        await backend.task.removeTask(taskData.taskRn);
      } finally {
        setEditingTaskRn((previousTaskRn) => (previousTaskRn === taskData.taskRn ? null : previousTaskRn));
      }
    });
  };

  const handleDueDateUpdate = (task, nextDueDate) => {
    const taskRn = task.taskRn;
    const currentTask = [
      ...lastWeekTasks,
      ...todayTasks,
      ...thisWeekTasks,
    ].find((task) => task.taskRn === taskRn);

    progressWrap(async () => {
      const updatedTaskResponse = await backend.task.updateDueDate(taskRn, nextDueDate);
      const updatedTask = await hydrateTaskAssignees(updatedTaskResponse.task);
      const nextDueDateMoment = moment(updatedTask.dueDate);

      setLastWeekTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== taskRn);
        if (!nextDueDateMoment.isBefore(moment().startOf('day').utc()) || updatedTask.status === 'COMPLETE') {
          return filteredTasks;
        }
        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });
      setTodayTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== taskRn);
        if (!nextDueDateMoment.isSame(moment(), 'day')) {
          return filteredTasks;
        }
        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });
      setThisWeekTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== taskRn);
        if (nextDueDateMoment.isBefore(moment().startOf('day').utc()) || nextDueDateMoment.isSame(moment(), 'day')) {
          return filteredTasks;
        }
        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });
      setDueTodayCount((previousCount) => {
        const previousDueDate = moment(currentTask.dueDate);
        const previousToday = currentTask.status !== 'COMPLETE' && previousDueDate.isSame(moment(), 'day');
        const nextToday = updatedTask.status !== 'COMPLETE' && nextDueDateMoment.isSame(moment(), 'day');

        return Math.max(0, previousCount - (previousToday ? 1 : 0) + (nextToday ? 1 : 0));
      });
    }, {debugString: 'UPDATE_TASK_DUE_DATE'});
  };

  const handleCompleteTask = (task) => {
    return progressWrap(async () => {
      const updatedTaskResponse = await backend.task.updateTaskStatus(task.taskRn, 'COMPLETE');
      const updatedTask = await hydrateTaskAssignees(updatedTaskResponse.task);
      const nextDueDate = moment(updatedTask.dueDate);

      setLastWeekTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== task.taskRn);

        if (!nextDueDate.isBefore(moment().startOf('day').utc()) || updatedTask.status === 'COMPLETE') {
          return filteredTasks;
        }

        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });
      setTodayTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== task.taskRn);

        if (!nextDueDate.isSame(moment(), 'day')) {
          return filteredTasks;
        }

        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });
      setThisWeekTasks((previousTasks) => {
        const filteredTasks = previousTasks.filter((item) => item.taskRn !== task.taskRn);

        if (nextDueDate.isBefore(moment().startOf('day').utc()) || nextDueDate.isSame(moment(), 'day')) {
          return filteredTasks;
        }

        return [...filteredTasks, updatedTask].sort((a, b) => moment(a?.dueDate).valueOf() - moment(b?.dueDate).valueOf());
      });

      if (task.status !== 'COMPLETE' && updatedTask.status === 'COMPLETE') {
        if (nextDueDate.isSame(moment(), 'day')) {
          setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
        }

        setTotalTaskCount((previousCount) => Math.max(0, previousCount - 1));
        taskRefreshDecrementAllTasksCount();
      }
    }, {debugString: 'COMPLETE_ENGAGEMENT_TASK'});
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        pt: 4,
        pb: 20,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 400,
            }}
          >
            {translate('tasks.AllTasks.Header.Title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.AllTasks.Header.Summary', {totalTasks: totalTaskCount, dueToday: dueTodayCount})}
          </Typography>
        </Stack>
        {
          !isEditorOpen && !editingTaskRn && (
            <Button
              variant='contained'
              onClick={() => openDialogs.addTask('HEADER')}
              sx={{
                backgroundColor: '#DA7955',
                color: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              <Box component='span' mr='4px'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Button>
          )
        }
      </Stack>
      {
        isEditorOpen && editorSection === 'HEADER' && !editingTaskRn && (
          <TaskEditor
            onCancel={closeEditor}
            onSubmit={handleAddTask}
          />
        )
      }
      <Stack>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontWeight: 700,
            fontSize: '16px',
            color: 'grey.500',
          }}
        >
          {translate('tasks.AllTasks.Sections.LastWeek')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 180px) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {
            isLoading && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <TaskListSkeleton />
              </Box>
            )
          }
          {
            !isLoading && lastWeekTasks.length === 0 && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {translate('tasks.AllTasks.Empty')}
                </Typography>
              </Box>
            )
          }
          {
            !isLoading && lastWeekTasks.map((task) => (
              editingTaskRn === task.taskRn ?
                <Box key={task.taskRn} sx={{gridColumn: '1 / -1', mb: 1}}>
                  <TaskEditor
                    isEditing
                    task={task}
                    onCancel={closeEditor}
                    onSubmit={(payload) => handleEditTask(task.taskRn, payload)}
                  />
                </Box> :
                <TaskItem
                  key={task.taskRn}
                  task={task}
                  onClickEditTask={openDialogs.editTask}
                  onUpdateDueDate={(nextDueDate) => handleDueDateUpdate(task, nextDueDate)}
                  onCompleteTask={handleCompleteTask}
                  removeTask={handleRemoveTask}
                />
            ))
          }
          {
            isEditorOpen && editorSection === 'LAST_WEEK' && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1', mt: 1}}>
                <TaskEditor
                  onCancel={closeEditor}
                  onSubmit={handleAddTask}
                />
              </Box>
            )
          }
          {
            (!isEditorOpen || editorSection !== 'LAST_WEEK') && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Box
                  component='button'
                  onClick={() => openDialogs.addTask('LAST_WEEK')}
                  sx={{
                    'background': 'none',
                    'border': 'none',
                    'color': '#A8A49E',
                    'cursor': 'pointer',
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '4px',
                    'fontSize': '14px',
                    'p': 0,
                    'mt': 1,
                    'transition': 'color 120ms ease',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Box component='span'>+</Box>
                  <Translate expression='tasks.Common.NewTask' />
                </Box>
              </Box>
            )
          }
        </Box>
      </Stack>
      <Stack>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontWeight: 700,
            fontSize: '16px',
            color: 'grey.500',
          }}
        >
          {translate('tasks.AllTasks.Sections.Today')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 180px) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {
            isLoading && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <TaskListSkeleton />
              </Box>
            )
          }
          {
            !isLoading && todayTasks.length === 0 && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {translate('tasks.AllTasks.Empty')}
                </Typography>
              </Box>
            )
          }
          {
            !isLoading && todayTasks.map((task) => (
              editingTaskRn === task.taskRn ?
                <Box key={task.taskRn} sx={{gridColumn: '1 / -1', mb: 1}}>
                  <TaskEditor
                    isEditing
                    task={task}
                    onCancel={closeEditor}
                    onSubmit={(payload) => handleEditTask(task.taskRn, payload)}
                  />
                </Box> :
                <TaskItem
                  key={task.taskRn}
                  task={task}
                  onClickEditTask={openDialogs.editTask}
                  onUpdateDueDate={(nextDueDate) => handleDueDateUpdate(task, nextDueDate)}
                  onCompleteTask={handleCompleteTask}
                  removeTask={handleRemoveTask}
                />
            ))
          }
          {
            isEditorOpen && editorSection === 'TODAY' && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1', mt: 1}}>
                <TaskEditor
                  onCancel={closeEditor}
                  onSubmit={handleAddTask}
                />
              </Box>
            )
          }
          {
            (!isEditorOpen || editorSection !== 'TODAY') && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Box
                  component='button'
                  onClick={() => openDialogs.addTask('TODAY')}
                  sx={{
                    'background': 'none',
                    'border': 'none',
                    'color': '#A8A49E',
                    'cursor': 'pointer',
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '4px',
                    'fontSize': '14px',
                    'p': 0,
                    'mt': 1,
                    'transition': 'color 120ms ease',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Box component='span'>+</Box>
                  <Translate expression='tasks.Common.NewTask' />
                </Box>
              </Box>
            )
          }
        </Box>
      </Stack>
      <Stack>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontWeight: 700,
            fontSize: '16px',
            color: 'grey.500',
          }}
        >
          {translate('tasks.AllTasks.Sections.ThisWeek')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 180px) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {
            isLoading && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <TaskListSkeleton />
              </Box>
            )
          }
          {
            !isLoading && thisWeekTasks.length === 0 && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {translate('tasks.AllTasks.Empty')}
                </Typography>
              </Box>
            )
          }
          {
            !isLoading && thisWeekTasks.map((task) => (
              editingTaskRn === task.taskRn ?
                <Box key={task.taskRn} sx={{gridColumn: '1 / -1', mb: 1}}>
                  <TaskEditor
                    isEditing
                    task={task}
                    onCancel={closeEditor}
                    onSubmit={(payload) => handleEditTask(task.taskRn, payload)}
                  />
                </Box> :
                <TaskItem
                  key={task.taskRn}
                  task={task}
                  onClickEditTask={openDialogs.editTask}
                  onUpdateDueDate={(nextDueDate) => handleDueDateUpdate(task, nextDueDate)}
                  onCompleteTask={handleCompleteTask}
                  removeTask={handleRemoveTask}
                />
            ))
          }
          {
            isEditorOpen && editorSection === 'THIS_WEEK' && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1', mt: 1}}>
                <TaskEditor
                  onCancel={closeEditor}
                  onSubmit={handleAddTask}
                />
              </Box>
            )
          }
          {
            (!isEditorOpen || editorSection !== 'THIS_WEEK') && !editingTaskRn && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Box
                  component='button'
                  onClick={() => openDialogs.addTask('THIS_WEEK')}
                  sx={{
                    'background': 'none',
                    'border': 'none',
                    'color': '#A8A49E',
                    'cursor': 'pointer',
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '4px',
                    'fontSize': '14px',
                    'p': 0,
                    'mt': 1,
                    'transition': 'color 120ms ease',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Box component='span'>+</Box>
                  <Translate expression='tasks.Common.NewTask' />
                </Box>
              </Box>
            )
          }
        </Box>
      </Stack>
    </Stack>
  );
};

AllTasks.propTypes = {
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  taskRefreshSetAllTasksCount: PropTypes.func.isRequired,
  taskRefreshIncrementAllTasksCount: PropTypes.func.isRequired,
  taskRefreshDecrementAllTasksCount: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(AllTasks));
